// The notice after a review decision followed through an email thread (sc-11471):
// "Also marked 4 replies in this thread Responsive · Undo". Bottom-center, polite,
// and gone after a few seconds or when dismissed.
import { useEffect } from 'react';
import { CornerDownRight, X } from 'lucide-react';

export interface ThreadNotice {
  text: string;
  /** Source emails whose thread tagging the Undo clears (empty: nothing to undo). */
  sourceIds: string[];
  /** Restarts the timer when the same text comes back. */
  key: number;
}

const VISIBLE_MS = 10000;

export function ThreadTagNotice({
  notice,
  onUndo,
  onDismiss,
}: {
  notice: ThreadNotice | null;
  onUndo: (sourceIds: string[]) => void;
  onDismiss: () => void;
}) {
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(onDismiss, VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [notice, onDismiss]);

  if (!notice) return null;
  // Cleared replies carry no source any more, so only a tagging notice offers Undo.
  const canUndo = notice.sourceIds.length > 0;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="thread-tag-notice"
      className="fixed bottom-5 left-1/2 z-50 -translate-x-1/2 flex items-center gap-3 rounded-lg bg-gray-900 px-4 py-2.5 text-sm text-white shadow-lg"
    >
      <CornerDownRight className="h-4 w-4 flex-shrink-0 text-emerald-300" aria-hidden />
      <span>{notice.text}</span>
      {canUndo && (
        <button
          type="button"
          data-testid="thread-tag-undo"
          className="font-semibold text-emerald-300 hover:text-emerald-200"
          onClick={() => onUndo(notice.sourceIds)}
        >
          Undo
        </button>
      )}
      <button type="button" aria-label="Dismiss" className="text-gray-400 hover:text-white" onClick={onDismiss}>
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
